import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  AddExerciseDTO,
  GetExercisesResponseDTO,
  UpdateExerciseDTO,
} from '../admin.dto';
import { AdminService } from '../admin.service';
import {
  AlertController,
  IonModal,
  LoadingController,
  ToastController,
} from '@ionic/angular';
import { Subscription } from 'rxjs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-exercises',
  templateUrl: './exercises.page.html',
  styleUrls: ['./exercises.page.scss'],
})
export class ExercisesPage implements OnInit, OnDestroy {
  isLoading = true;
  newDirections: string;
  loadedExercises: GetExercisesResponseDTO[];
  allExercises: GetExercisesResponseDTO[];
  exercisesSub: Subscription;
  addExerciseSub: Subscription;
  updateExerciseSub: Subscription;
  trustedLink: SafeResourceUrl;
  uploadedImage: string = '';
  showImageError = false;
  isPreviewMode = false;
  selectedName = '';
  selectedDesc = '';
  selectedUrl = '';
  selectedGoal = '';
  selectedId = 0;
  sendNotif = true;
  isModalOpen = false;
  isUpdate = false;
  filter = 'dogs';
  selectedPetTypeId = 1; // Default to dogs
  originalImage = '';
  @ViewChild('newExerciseModal') newExerciseModal: IonModal;
  constructor(
    private adminService: AdminService,
    private domSanitizer: DomSanitizer,
    private toastController: ToastController,
    private loadingCtrl: LoadingController,
    private alertCtrl: AlertController
  ) { }

  ngOnInit() {
    this.filter = localStorage.getItem('animalFilter') || 'dogs';
    this.getExercises();
  }

  AddNew() {
    this.ClearFields();
    this.isUpdate = false;
    this.isModalOpen = true;
  }
  ngOnDestroy() {
    if (this.exercisesSub) this.exercisesSub.unsubscribe();
    if (this.addExerciseSub) this.addExerciseSub.unsubscribe();
    if (this.updateExerciseSub) this.updateExerciseSub.unsubscribe();
  }
  getExercises() {
    this.isLoading = true;
    this.exercisesSub = this.adminService
      .getAllExercises()
      .subscribe((exercises) => {
        this.allExercises = exercises;
        this.filterExercises();
        this.isLoading = false;
      });
  }

  ViewExercise(exerciseId: number) {
    this.loadingCtrl
      .create({
        keyboardClose: true,
        message: 'Please wait...',
      })
      .then((loadingEl) => {
        loadingEl.present();
        this.exercisesSub = this.adminService
          .getExerciseById(exerciseId)
          .subscribe((exercise: GetExercisesResponseDTO) => {
            this.selectedDesc = exercise.description;
            this.selectedGoal = exercise.goal;
            this.selectedName = exercise.name;
            this.selectedUrl = exercise.videoUrl;
            this.selectedId = exercise.id;
            this.uploadedImage = exercise.thumbnailImage;
            this.originalImage = exercise.thumbnailImage;
            this.selectedPetTypeId = exercise.petTypeId;
            this.isUpdate = true;
            this.isModalOpen = true;
            loadingEl.dismiss();
          });
      });
  }
  cancel() {
    if (this.isPreviewMode) {
      this.isPreviewMode = false;
    } else {
      this.isModalOpen = false;
      this.ClearFields();
    }
  }

  ClearFields() {
    this.uploadedImage = '';
    this.showImageError = false;
    this.isPreviewMode = false;
    this.selectedName = '';
    this.selectedDesc = '';
    this.selectedUrl = '';
    this.selectedGoal = '';
    this.sendNotif = true;
  }
  onPhotoCaptured(event: { imageUri: string }) {
    this.uploadedImage = event.imageUri;
  }

  previewExercise() {
    if (this.uploadedImage.length == 0) {
      this.showImageError = true;
    } else {
      this.trustedLink = this.domSanitizer.bypassSecurityTrustResourceUrl(
        this.selectedUrl
      );
      const lines = this.selectedDesc.split('\n');

      // Separate last 2 lines (emoji lines)
      const emojiLines = lines.slice(-2);
      const normalLines = lines.slice(0, -2);

      const formatted = normalLines.map(line => {
        const match = line.match(/^(\d+)-\s*(.*)/);
        if (match) {
          const stepNumber = match[1];
          const stepBody = match[2];

          const [title, ...rest] = stepBody.split("–");
          const stepTitle = title.trim();
          const description = rest.join("–").trim();

          if (description) {
            return `<div class="instruction-line"><strong>Step ${stepNumber}:</strong> ${stepTitle} – ${description}</div>`;
          } else {
            return `<div class="instruction-line"><strong>Step ${stepNumber}:</strong> ${stepTitle}</div>`;
          }
        } else {
          return `<div class="instruction-line">${line}</div>`;
        }
      });

      // Add emoji block with both lines wrapped in instruction-line divs
      const emojiBlock = `
  <div class="emoji">
    ${emojiLines.map(l => `<div class="emoji-line">${l}</div>`).join('')}
  </div>
`;

      formatted.push(emojiBlock);

      this.newDirections = formatted.join('\n');
      this.isPreviewMode = true;
    }
  }

  UpdateExercise() {
    const updatedExercise: UpdateExerciseDTO = {
      id: this.selectedId,
      description: this.selectedDesc,
      goal: this.selectedGoal,
      videoUrl: this.selectedUrl,
      name: this.selectedName,
      thumbnailImage: this.uploadedImage,
      petTypeId: this.selectedPetTypeId,
    };
    this.loadingCtrl
      .create({
        keyboardClose: true,
        showBackdrop: false,
        message: 'Please Wait...',
      })
      .then(async (loadingEl) => {
        loadingEl.present();
        this.updateExerciseSub = (
          await this.adminService.updateExercise(updatedExercise, this.originalImage)
        ).subscribe({
          next: async (isAdded) => {
            if (!isAdded) {
              loadingEl.dismiss();
              this.alertCtrl
                .create({
                  header: 'An Error Occurred',
                  message:
                    'Could not perform this action at the moment. Please try again later.',
                  buttons: [{ text: 'Dismiss' }],
                })
                .then((alerEl) => {
                  loadingEl.dismiss();
                  alerEl.present();
                });
            } else {
              const toast = await this.toastController.create({
                color: 'primary',
                duration: 2000,
                message: 'Exercise updated successfully',
              });
              this.getExercises();
              loadingEl.dismiss();
              await toast.present();
              this.ClearFields();
              this.isUpdate = false;
              this.isModalOpen = false;
            }
          },
          error: () => loadingEl.dismiss(),
        });
      });
  }

  deleteExercise(exerciseId: number) {
    this.alertCtrl
      .create({
        header: 'Delete Exercise?',
        message: 'Are you sure you want to delete this exercise?',
        buttons: [
          {
            text: 'Cancel',
            handler: () => {
              this.alertCtrl.dismiss();
            },
          },
          {
            text: 'Delete',
            cssClass: 'confirm-delete',
            handler: () => {
              this.performDelete(exerciseId);
              this.alertCtrl.dismiss();
            },
          },
        ],
      })
      .then((alertEl) => alertEl.present());
  }

  performDelete(exerciseId: number) {
    this.loadingCtrl
      .create({
        keyboardClose: true,
        message: 'Please wait...',
      })
      .then((loadingEl) => {
        loadingEl.present();
        this.originalImage = this.loadedExercises.find((e) => e.id === exerciseId)
          .thumbnailImage;
        this.adminService.deleteExercise(exerciseId, this.originalImage).subscribe({
          next: (res) => {
            if (res) {
              const index = this.loadedExercises.findIndex(
                (e) => e.id === exerciseId
              );
              this.loadedExercises.splice(index, 1);
            }
            loadingEl.dismiss();
          },
          error: () => {
            loadingEl.dismiss();
          },
        });
      });
  }

  AddExercise() {
    this.loadingCtrl
      .create({
        keyboardClose: true,
        showBackdrop: false,
        message: 'Please Wait...',
      })
      .then(async (loadingEl) => {
        loadingEl.present();
        const exercise: AddExerciseDTO = {
          description: this.selectedDesc,
          goal: this.selectedGoal,
          name: this.selectedName,
          sendNotification: this.sendNotif,
          thumbnailImage: this.uploadedImage,
          petTypeId: this.selectedPetTypeId,
          videoUrl: this.selectedUrl,
        };
        this.addExerciseSub = (
          await this.adminService.addExercise(exercise)
        ).subscribe({
          next: async (isAdded) => {
            if (!isAdded) {
              loadingEl.dismiss();
              this.alertCtrl
                .create({
                  header: 'An Error Occurred',
                  message:
                    'Could not perform this action at the moment. Please try again later.',
                  buttons: [{ text: 'Dismiss' }],
                })
                .then((alerEl) => {
                  loadingEl.dismiss();
                  alerEl.present();
                });
            } else {
              const toast = await this.toastController.create({
                color: 'primary',
                duration: 2000,
                message: 'Exercise added successfully',
              });
              this.getExercises();
              loadingEl.dismiss();
              await toast.present();
              this.isPreviewMode = false;
              this.cancel();
            }
          },
          error: () => loadingEl.dismiss(),
        });
      });
  }

  filterExercises() {
    if (this.filter === 'dogs') {
      this.loadedExercises = this.allExercises.filter(
        (exercise) => exercise.petTypeId === 1
      );
      localStorage.setItem('animalFilter', this.filter);
    } else if (this.filter === 'cats') {
      this.loadedExercises = this.allExercises.filter(
        (exercise) => exercise.petTypeId === 2
      );
      localStorage.setItem('animalFilter', this.filter);
    } else {
      this.loadedExercises = this.allExercises;
    }
  }
}
